import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Briefcase, Clock, CheckCircle2, ArrowLeft, Calendar, Video } from "lucide-react";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const fmtDay = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const TYPES = {
  student_assessment: { label: "Student Assessment Call", icon: GraduationCap, blurb: "Explore your strengths, degrees and career direction." },
  business: { label: "Business Meeting", icon: Briefcase, blurb: "Discuss corporate programs, partnerships or consulting." },
};

export default function BookCall() {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [data, setData] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!type) return;
    setData(null); setActiveDay(null); setSlot(null);
    axios.get(`${API}/meetings/slots`, { params: { meeting_type: type } })
      .then((r) => setData(r.data)).catch(() => setData({ slots: [], demo: true }));
  }, [type]);

  const byDay = useMemo(() => {
    const m = {};
    (data?.slots || []).forEach((s) => { const k = new Date(s).toDateString(); (m[k] = m[k] || []).push(s); });
    return m;
  }, [data]);
  const [activeDay, setActiveDay] = useState(null);
  useEffect(() => { const days = Object.keys(byDay); if (days.length && !activeDay) setActiveDay(days[0]); }, [byDay, activeDay]);

  const chooseType = (k) => { setType(k); track("Meeting", `Selected Type: ${k}`); setStep(2); };

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/meetings/book`, {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        meeting_type: type, notes: form.notes.trim(), start: slot, timezone: TZ,
      });
      track("Meeting", "Call Booked", { user_email: form.email.trim() });
      setDone(r.data.meeting);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not book that time. Please try another slot.");
    } finally { setBusy(false); }
  };

  const T = type ? TYPES[type] : null;

  return (
    <div className="min-h-screen bg-[#F7F5F2] text-[#050F24]">
      <header className="bg-[#050F24] text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="book-home-link" className="font-head font-black tracking-tight text-lg">The Next Move <span className="text-[#D94833]">by Rhellex</span></Link>
          <Link to="/" className="text-xs text-white/70 hover:text-white flex items-center gap-1.5"><ArrowLeft size={14} /> Back to site</Link>
        </div>
      </header>

      <div className="bg-[#050F24] text-white">
        <div className="max-w-5xl mx-auto px-6 pb-14 pt-6">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#D94833] font-bold">Schedule a call with Rhea</p>
          <h1 className="font-head text-4xl sm:text-5xl font-black mt-3 max-w-2xl leading-tight">Book a time that works for you</h1>
          <p className="text-white/70 mt-4 max-w-xl text-base">Pick a slot for a student assessment or a business meeting. You will get a confirmation and a video link. Times are shown in your timezone.</p>
          <div className="h-1 w-16 bg-[#D94833] mt-6" />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 -mt-8 pb-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm p-6 sm:p-9">
          {done ? (
            <div className="text-center py-8" data-testid="book-success">
              <div className="w-14 h-14 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto"><CheckCircle2 className="text-green-600" size={30} /></div>
              <h2 className="font-head text-2xl font-black mt-4">You're booked.</h2>
              <p className="text-[#3A4A63] mt-2">{done.meeting_label} on <strong>{fmtDay(done.start)} at {fmtTime(done.start)}</strong> ({TZ}).</p>
              {done.meet_link ? (
                <a href={done.meet_link} data-testid="book-meet-link" className="inline-flex items-center gap-2 mt-5 bg-[#050F24] text-white px-5 py-3 text-sm font-semibold rounded-lg hover:bg-[#D94833] transition-colors"><Video size={16} /> Join Google Meet</a>
              ) : (
                <p className="text-sm text-[#9CA3AF] mt-4">A Google Calendar invite with the meeting link will arrive by email shortly.</p>
              )}
              <p className="text-xs text-[#9CA3AF] mt-6">A confirmation was sent to {form.email}.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs font-semibold text-[#9CA3AF] mb-6">
                <span className={step >= 1 ? "text-[#D94833]" : ""}>1 Type</span><span>/</span>
                <span className={step >= 2 ? "text-[#D94833]" : ""}>2 Time</span><span>/</span>
                <span className={step >= 3 ? "text-[#D94833]" : ""}>3 Details</span>
              </div>

              {step === 1 && (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="book-type-step">
                  {Object.entries(TYPES).map(([k, t]) => {
                    const Icon = t.icon;
                    return (
                      <button key={k} data-testid={`book-type-${k}`} onClick={() => chooseType(k)}
                        className="text-left border border-[#E5E7EB] rounded-xl p-6 hover:border-[#D94833] hover:shadow-md transition-all group">
                        <Icon className="text-[#D94833]" size={26} />
                        <p className="font-head text-lg font-bold mt-3 group-hover:text-[#D94833] transition-colors">{t.label}</p>
                        <p className="text-sm text-[#3A4A63] mt-1">{t.blurb}</p>
                        <p className="text-xs text-[#9CA3AF] mt-3 flex items-center gap-1.5"><Clock size={13} /> {data?.duration_minutes || 30} minutes</p>
                      </button>
                    );
                  })}
                </div>
              )}

              {step === 2 && (
                <div data-testid="book-time-step">
                  <button onClick={() => setStep(1)} className="text-xs text-[#9CA3AF] hover:text-[#050F24] flex items-center gap-1.5 mb-4"><ArrowLeft size={13} /> {T?.label}</button>
                  {Object.keys(byDay).length === 0 ? (
                    <p className="text-sm text-[#9CA3AF] py-8 text-center">No open times right now. Please check back soon or contact Rhea's team.</p>
                  ) : (
                    <>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {Object.keys(byDay).map((d) => (
                          <button key={d} data-testid={`book-day-${d}`} onClick={() => setActiveDay(d)}
                            className={`shrink-0 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${activeDay === d ? "bg-[#050F24] text-white border-[#050F24]" : "border-[#E5E7EB] hover:border-[#050F24]"}`}>
                            <Calendar size={13} className="inline mr-1.5 -mt-0.5" />{fmtDay(byDay[d][0])}
                          </button>
                        ))}
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4">
                        {(byDay[activeDay] || []).map((s) => (
                          <button key={s} data-testid={`book-slot-${s}`} onClick={() => { setSlot(s); setStep(3); }}
                            className="border border-[#E5E7EB] rounded-lg py-2.5 text-sm font-semibold hover:border-[#D94833] hover:text-[#D94833] transition-colors">
                            {fmtTime(s)}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-[#9CA3AF] mt-4">Times shown in your timezone ({TZ}).</p>
                    </>
                  )}
                </div>
              )}

              {step === 3 && (
                <div data-testid="book-details-step">
                  <button onClick={() => setStep(2)} className="text-xs text-[#9CA3AF] hover:text-[#050F24] flex items-center gap-1.5 mb-4"><ArrowLeft size={13} /> {fmtDay(slot)} at {fmtTime(slot)}</button>
                  <div className="space-y-3 max-w-md">
                    <input data-testid="book-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm" />
                    <input data-testid="book-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm" />
                    <input data-testid="book-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm" />
                    <textarea data-testid="book-notes" placeholder="What would you like to cover? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm" />
                    {err && <p data-testid="book-error" className="text-sm text-[#D94833]">{err}</p>}
                    <button data-testid="book-confirm" onClick={submit} disabled={busy || !form.name.trim() || !form.email.trim()}
                      className="w-full bg-[#D94833] text-white py-3.5 rounded-lg text-sm font-bold hover:bg-[#C03F2A] transition-colors disabled:opacity-50">
                      {busy ? "Booking…" : `Confirm ${T?.label}`}
                    </button>
                    <p className="text-xs text-[#9CA3AF]">{T?.label} · {fmtDay(slot)} at {fmtTime(slot)} ({TZ})</p>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
